import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Link2, Unlink, ChevronDown, ArrowRight, Check, Clock, AlertCircle, Building2, FileText, CreditCard, X, Zap, Target, Ban, Undo2, Save, Calendar, CheckCircle, Split, Plus, Minus, Wallet, RotateCcw, Banknote, Mail, Send, MessageSquare } from 'lucide-react';
import { cobrosReclamacionAPI, clientesAPI, getAuthToken } from '../api/api';
import { parsearFechaServidor } from '../utils/pantallasUnDia';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const token = () => getAuthToken();
const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' });

const fetcher = async (url, opts = {}) => {
  const res = await fetch(`${API_URL}${url}`, { headers: headers(), ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Error ${res.status}`);
  }
  return res.json();
};

const formatCurrency = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

// U-102 (lote 7): una fecha con hora y sin zona la guardó el servidor en UTC
const formatFechaHora = (iso) => {
  if (!iso) return '';
  const d = parsearFechaServidor(iso);
  if (!d) return String(iso);
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Convierte el detail de un error axios en texto seguro para renderizar.
// FastAPI devuelve en los 422 una LISTA de objetos: si se pinta tal cual,
// React casca con pantalla en blanco ("objects are not valid as a child").
const detalleError = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg || '').filter(Boolean);
    if (msgs.length) return msgs.join(' · ');
  }
  return e?.message || fallback;
};

const diasDesde = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const diasBadge = (dias, titulo) => {
  if (dias === null) return null;
  const cls = dias > 60 ? 'bg-red-100 text-red-700' : dias > 30 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${cls} mr-1`} title={titulo}>
      {dias} {dias === 1 ? 'dia' : 'dias'}
    </span>
  );
};

// Badge para facturas pendientes que AÚN no han vencido (dias_vencida <= 0).
const venceBadge = (diasVencida, vencimientoTexto) => {
  const faltan = -(diasVencida || 0);
  const texto = faltan <= 0 ? 'Vence hoy' : `Vence en ${faltan} ${faltan === 1 ? 'dia' : 'dias'}`;
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-100 text-sky-700 mr-1"
      title={vencimientoTexto ? `Aun no vencida (vence el ${vencimientoTexto})` : 'Aun no vencida'}
    >
      {texto}
    </span>
  );
};

// Badge para facturas de clientes sin política de pago: sin vencimiento pactado,
// pendientes (reclamables) desde el día de emisión.
const emitidaBadge = (diasDesdeEmision) => {
  const dias = diasDesdeEmision || 0;
  const cls = dias > 60 ? 'bg-red-100 text-red-700' : dias > 30 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${cls} mr-1`} title="Sin política de pago: pendiente desde la emisión">
      {dias === 0 ? 'Emitida hoy' : `Emitida hace ${dias} ${dias === 1 ? 'dia' : 'dias'}`}
    </span>
  );
};

const estadoBadge = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e === 'pagado' || e === 'si') return <span data-testid="estado-pagado" className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">Pagado</span>;
  if (e === 'parcial' || e === 'pago_parcial') return <span data-testid="estado-parcial" className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Parcial</span>;
  if (e === 'impagado') return <span data-testid="estado-impagado" className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-800 text-white">Impagado</span>;
  return <span data-testid="estado-pendiente" className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Pendiente</span>;
};

export default function ControlCobrosUnificado() {
  const [resumen, setResumen] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [impagados, setImpagados] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [buscarPendiente, setBuscarPendiente] = useState('');
  const [buscarIngreso, setBuscarIngreso] = useState('');
  const [buscarImpagado, setBuscarImpagado] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedPres, setSelectedPres] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [vinculandoIngreso, setVinculandoIngreso] = useState(null);
  // Fila de Pendientes seleccionada (multievento por jornada: una por factura)
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [showIngresos, setShowIngresos] = useState(false);
  const [incluirVinculados, setIncluirVinculados] = useState(false);
  const [activeTab, setActiveTab] = useState('pendientes');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearsDisponibles, setYearsDisponibles] = useState([]);
  const [showImpagadoModal, setShowImpagadoModal] = useState(null);
  const [obsImpagado, setObsImpagado] = useState('');
  const [cobrados, setCobrados] = useState([]);
  const [buscarCobrado, setBuscarCobrado] = useState('');
  const [multiLinkIngreso, setMultiLinkIngreso] = useState(null);
  const [multiLinkSelections, setMultiLinkSelections] = useState([]);
  const [multiLinkSearch, setMultiLinkSearch] = useState('');
  const [multiLinkPendientes, setMultiLinkPendientes] = useState([]);
  const [excesoData, setExcesoData] = useState(null);
  const [showFuturos, setShowFuturos] = useState(false);
  // Reclamaciones de cobro
  const [reclamaciones, setReclamaciones] = useState([]);
  const [loadingReclamaciones, setLoadingReclamaciones] = useState(false);
  const [reclamacionSel, setReclamacionSel] = useState(null);
  // El contador de la pestaña cuenta las reclamables ya: vencidas o sin política
  // de pago (pendientes desde la emisión). Las "por vencer" con política se
  // listan pero no se cuentan.
  const numReclamablesYa = reclamaciones.filter(f => f.vencida || !f.vencimiento).length;
  const [politicaFactura, setPoliticaFactura] = useState(null);
  const [aviso, setAviso] = useState(null); // {tipo: 'ok'|'error', texto}

  const mostrarAviso = useCallback((tipo, texto) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 5000);
  }, []);

  const fetchResumen = useCallback(async () => {
    try {
      const params = selectedYear ? `?year=${selectedYear}` : '';
      const data = await fetcher(`/api/cobros-unificado/resumen${params}`);
      setResumen(data);
      if (data.years_disponibles?.length > 0) {
        setYearsDisponibles(data.years_disponibles);
      }
    } catch (e) { console.error(e); }
  }, [selectedYear]);

  const fetchPendientes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (buscarPendiente) params.set('buscar', buscarPendiente);
      if (selectedYear) params.set('year', selectedYear);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await fetcher(`/api/cobros-unificado/pendientes${qs}`);
      setPendientes(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [buscarPendiente, selectedYear]);

  const fetchImpagados = useCallback(async () => {
    try {
      const params = buscarImpagado ? `?buscar=${encodeURIComponent(buscarImpagado)}` : '';
      const data = await fetcher(`/api/cobros-unificado/impagados${params}`);
      setImpagados(data.items || []);
    } catch (e) { console.error(e); }
  }, [buscarImpagado]);

  const fetchCobrados = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (buscarCobrado) params.set('buscar', buscarCobrado);
      if (selectedYear) params.set('year', selectedYear);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await fetcher(`/api/cobros-unificado/cobrados${qs}`);
      setCobrados(data.items || []);
    } catch (e) { console.error(e); }
  }, [buscarCobrado, selectedYear]);

  const fetchIngresos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (buscarIngreso) params.set('buscar', buscarIngreso);
      if (incluirVinculados) params.set('incluir_vinculados', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await fetcher(`/api/cobros-unificado/ingresos-disponibles${qs}`);
      setIngresos(data || []);
    } catch (e) { console.error(e); }
  }, [buscarIngreso, incluirVinculados]);

  const fetchReclamaciones = useCallback(async () => {
    setLoadingReclamaciones(true);
    try {
      const res = await cobrosReclamacionAPI.vencidas();
      const facturas = res.data?.facturas || [];
      setReclamaciones(facturas);
      // Mantener sincronizado el detalle seleccionado con el item fresco.
      setReclamacionSel(prev => {
        if (!prev) return prev;
        const fresco = facturas.find(f => f.factura_id === prev.factura_id);
        return fresco || null;
      });
    } catch (e) { console.error(e); }
    setLoadingReclamaciones(false);
  }, []);

  const fetchDetalle = async (presId) => {
    try {
      const data = await fetcher(`/api/cobros-unificado/presupuesto/${presId}/detalle`);
      setDetalle(data);
      setSelectedPres(presId);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchResumen(); }, [fetchResumen]);
  useEffect(() => { fetchPendientes(); }, [fetchPendientes]);
  useEffect(() => { if (activeTab === 'impagados') fetchImpagados(); }, [activeTab, fetchImpagados]);
  useEffect(() => { if (activeTab === 'cobrados') fetchCobrados(); }, [activeTab, fetchCobrados]);
  useEffect(() => { if (showIngresos) fetchIngresos(); }, [showIngresos, fetchIngresos]);
  useEffect(() => { if (activeTab === 'reclamaciones') fetchReclamaciones(); }, [activeTab, fetchReclamaciones]);

  // Con factura de jornada: el ingreso va a esa factura y, si sobra, se
  // reparte entre las demas jornadas por su importe real (repartir_excedente).
  const vincular = async (ingresoId, presId, importe, facturaId = null) => {
    try {
      const result = await fetcher('/api/cobros-unificado/vincular', {
        method: 'POST',
        body: JSON.stringify({
          presupuesto_id: presId,
          ingreso_id: ingresoId,
          importe,
          ...(facturaId ? { factura_id: facturaId, repartir_excedente: true } : {}),
        })
      });
      setVinculandoIngreso(null);
      setShowIngresos(false);
      setFilaSeleccionada(null);
      fetchResumen(); fetchPendientes(); fetchIngresos();
      if (selectedPres === presId) fetchDetalle(presId);
      // Check for excess payment
      if (result.exceso && result.exceso.importe > 0.01) {
        setExcesoData(result.exceso);
      }
    } catch (e) { alert(e.message); }
  };

  const desvincular = async (vinculoId) => {
    if (!window.confirm('Desvincular este ingreso?')) return;
    try {
      await fetcher('/api/cobros-unificado/desvincular', {
        method: 'POST',
        body: JSON.stringify({ vinculo_id: vinculoId })
      });
      fetchResumen(); fetchPendientes(); fetchIngresos();
      if (selectedPres) fetchDetalle(selectedPres);
    } catch (e) { alert(e.message); }
  };

  const sincronizarTodos = async () => {
    if (!window.confirm('Sincronizar estado de pago de TODOS los presupuestos con facturas?')) return;
    setSyncing(true);
    try {
      const data = await fetcher('/api/cobros-unificado/sincronizar-todos', { method: 'POST' });
      alert(data.message);
      fetchResumen(); fetchPendientes();
    } catch (e) { alert(e.message); }
    setSyncing(false);
  };

  const marcarImpagado = async (presupuestoId, observaciones) => {
    try {
      await fetcher('/api/cobros-unificado/marcar-impagado', {
        method: 'POST',
        body: JSON.stringify({ presupuesto_id: presupuestoId, observaciones })
      });
      setShowImpagadoModal(null);
      setObsImpagado('');
      fetchResumen(); fetchPendientes(); fetchImpagados();
    } catch (e) { alert(e.message); }
  };

  const desmarcarImpagado = async (presupuestoId) => {
    if (!window.confirm('Devolver este presupuesto a pendiente de cobro?')) return;
    try {
      await fetcher('/api/cobros-unificado/desmarcar-impagado', {
        method: 'POST',
        body: JSON.stringify({ presupuesto_id: presupuestoId })
      });
      fetchResumen(); fetchPendientes(); fetchImpagados();
    } catch (e) { alert(e.message); }
  };

  const desmarcarPagado = async (presupuestoId) => {
    if (!window.confirm('¿Desmarcar este presupuesto como pagado? Se recalculará el estado real desde los vínculos de ingresos.')) return;
    try {
      const result = await fetcher('/api/cobros-unificado/desmarcar-pagado', {
        method: 'POST',
        body: JSON.stringify({ presupuesto_id: presupuestoId })
      });
      alert(result.message);
      fetchResumen(); fetchPendientes(); fetchCobrados(); fetchImpagados();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="control-cobros-unificado">
      <div className="w-full px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Control de Cobros</h1>
            <p className="text-sm text-slate-500 mt-1">Sistema unificado de seguimiento de pagos</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Year selector */}
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <select
                data-testid="year-selector"
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="text-sm font-medium text-slate-700 bg-transparent border-none outline-none cursor-pointer"
              >
                <option value="">Todos los anos</option>
                {yearsDisponibles.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              data-testid="sync-all-btn"
              onClick={sincronizarTodos}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar Todo'}
            </button>
          </div>
        </div>

        {/* Aviso global (reclamaciones) */}
        {aviso && (
          <div
            data-testid="aviso-reclamacion"
            className={`rounded-lg px-4 py-3 text-sm border ${aviso.tipo === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}
          >
            {aviso.texto}
          </div>
        )}

        {/* KPIs */}
        {resumen && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-section">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Facturado</p>
              <p className="text-xl font-bold text-slate-800 mt-1">{formatCurrency(resumen.total_facturado)}</p>
              {selectedYear && <p className="text-[10px] text-slate-400 mt-0.5">Ano {selectedYear}</p>}
            </div>
            <div className="bg-white rounded-xl p-4 border border-emerald-200">
              <p className="text-xs text-emerald-600 uppercase tracking-wide">Cobrado</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{formatCurrency(resumen.total_cobrado)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-red-200">
              <p className="text-xs text-red-600 uppercase tracking-wide">Pendiente</p>
              <p className="text-xl font-bold text-red-700 mt-1">{formatCurrency(resumen.pendiente_desglose?.total ?? resumen.total_pendiente)}</p>
              {resumen.pendiente_desglose && (
                <div className="mt-1 space-y-0.5 text-[11px] text-slate-500">
                  <p className="flex justify-between gap-2"><span>Factura real</span><span className="font-medium text-slate-700">{formatCurrency(resumen.pendiente_desglose.facturado)}</span></p>
                  <p className="flex justify-between gap-2"><span>Sin factura</span><span className="font-medium text-slate-700">{formatCurrency(resumen.pendiente_desglose.sin_factura)}</span></p>
                  <p className="flex justify-between gap-2"><span>Proformas</span><span className="font-medium text-slate-700">{formatCurrency(resumen.pendiente_desglose.proformas)}</span></p>
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="kpi-cobrado-futuro"
              onClick={() => setShowFuturos(v => !v)}
              className="bg-white rounded-xl p-4 border border-blue-200 text-left hover:border-blue-400 transition-colors"
              title="Dinero ya cobrado de eventos que aun no se han celebrado"
            >
              <p className="text-xs text-blue-600 uppercase tracking-wide flex items-center justify-between">
                Cobrado eventos futuros
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFuturos ? 'rotate-180' : ''}`} />
              </p>
              <p className="text-xl font-bold text-blue-700 mt-1">{formatCurrency(resumen.cobrado_eventos_futuros)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{(resumen.eventos_futuros_cobrados || []).length} eventos · ya en cuenta</p>
            </button>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Presupuestos</p>
              <div className="flex gap-3 mt-1 flex-wrap">
                <span className="text-sm"><span className="font-bold text-emerald-600">{resumen.presupuestos_pagados}</span> pag.</span>
                <span className="text-sm"><span className="font-bold text-amber-600">{resumen.presupuestos_parcial}</span> parc.</span>
                <span className="text-sm"><span className="font-bold text-red-600">{resumen.presupuestos_pendientes}</span> pend.</span>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-800">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Impagados</p>
              <p className="text-xl font-bold text-slate-800 mt-1">{resumen.presupuestos_impagados || 0}</p>
            </div>
          </div>
        )}

        {/* Detalle de cobros anticipados de eventos futuros */}
        {resumen && showFuturos && (
          <div className="bg-white rounded-xl border border-blue-200 overflow-hidden" data-testid="panel-eventos-futuros">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-800">Pagos recibidos de eventos futuros</p>
                <p className="text-xs text-blue-600 mt-0.5">Este dinero ya esta en la cuenta: no contar con el como ingreso el dia del evento.</p>
              </div>
              <button onClick={() => setShowFuturos(false)} className="p-1 text-blue-400 hover:text-blue-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            {(resumen.eventos_futuros_cobrados || []).length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-400 text-center">No hay pagos anticipados de eventos futuros.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                    <th className="px-4 py-2 font-medium">Presupuesto</th>
                    <th className="px-4 py-2 font-medium">Cliente</th>
                    <th className="px-4 py-2 font-medium">Fecha evento</th>
                    <th className="px-4 py-2 font-medium text-right">Cobrado</th>
                    <th className="px-4 py-2 font-medium text-right">Total evento</th>
                    <th className="px-4 py-2 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.eventos_futuros_cobrados.map((ev) => (
                    <tr key={ev.presupuesto_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-700">#{ev.numero}</td>
                      <td className="px-4 py-2 text-slate-600">{ev.cliente_nombre}</td>
                      <td className="px-4 py-2 text-slate-600">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-blue-400" />{(ev.fecha_evento || '').split('-').reverse().join('/')}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-blue-700">{formatCurrency(ev.total_pagado)}</td>
                      <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(ev.importe_total)}</td>
                      <td className="px-4 py-2">{estadoBadge(ev.estado_pago)}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50/60">
                    <td colSpan="3" className="px-4 py-2 text-xs font-semibold text-blue-800 uppercase tracking-wide">Total ya cobrado</td>
                    <td className="px-4 py-2 text-right font-bold text-blue-800">{formatCurrency(resumen.cobrado_eventos_futuros)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left panel */}
          <div className="lg:col-span-3 space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1">
              <button
                data-testid="tab-pendientes"
                onClick={() => setActiveTab('pendientes')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'pendientes' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <AlertCircle className="w-4 h-4" />
                Pendientes
                {pendientes.length > 0 && <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">{pendientes.length}</span>}
              </button>
              <button
                data-testid="tab-impagados"
                onClick={() => setActiveTab('impagados')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'impagados' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <Ban className="w-4 h-4" />
                Impagados
                {impagados.length > 0 && <span className="px-1.5 py-0.5 text-xs rounded-full bg-slate-700 text-white">{impagados.length}</span>}
              </button>
              <button
                data-testid="tab-reclamaciones"
                onClick={() => setActiveTab('reclamaciones')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'reclamaciones' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <Mail className="w-4 h-4" />
                Reclamaciones
                {numReclamablesYa > 0 && <span className="px-1.5 py-0.5 text-xs rounded-full bg-amber-500 text-white">{numReclamablesYa}</span>}
              </button>
              <button
                data-testid="tab-cobrados"
                onClick={() => setActiveTab('cobrados')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'cobrados' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <CheckCircle className="w-4 h-4" />
                Cobrados
                {cobrados.length > 0 && <span className="px-1.5 py-0.5 text-xs rounded-full bg-emerald-600 text-white">{cobrados.length}</span>}
              </button>
            </div>

            {/* Pendientes Tab */}
            {activeTab === 'pendientes' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    Pendientes de Cobro
                  </h2>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      data-testid="search-pendientes"
                      type="text"
                      placeholder="Buscar num o cliente..."
                      value={buscarPendiente}
                      onChange={(e) => setBuscarPendiente(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchPendientes()}
                      className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-56"
                    />
                  </div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center text-slate-400">Cargando...</div>
                  ) : pendientes.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">No hay cobros pendientes</div>
                  ) : pendientes.map(p => (
                    <div
                      key={p.fila_id || p.presupuesto_id}
                      data-testid={`pendiente-${p.numero}${p.factura_id ? `-${p.factura_id}` : ''}`}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${(filaSeleccionada ? (filaSeleccionada.fila_id || filaSeleccionada.presupuesto_id) === (p.fila_id || p.presupuesto_id) : selectedPres === p.presupuesto_id) ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      onClick={() => { setFilaSeleccionada(p); fetchDetalle(p.presupuesto_id); }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <a
                            href={`/presupuestos/${p.presupuesto_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-bold text-slate-600 flex-shrink-0 hover:text-blue-600 hover:underline"
                            title="Abrir presupuesto en otra pestaña"
                          >
                            #{p.numero}
                          </a>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{p.cliente_nombre}</p>
                            <p className="text-xs text-slate-400">
                              {p.factura_numero && <span className="mr-2">{p.es_proforma ? 'Proforma' : 'Factura'}: {p.factura_numero}</span>}
                              {p.es_factura_jornada && p.jornada_fecha && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-sky-100 text-sky-700 mr-1" title="Factura de una jornada del multievento">
                                  Jornada {String(p.jornada_fecha).slice(0, 10).split('-').reverse().join('/')}
                                </span>
                              )}
                              {p.es_resto_jornadas && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-sky-100 text-sky-700 mr-1" title="Lo que falta de las jornadas que aun no tienen factura">
                                  Jornadas sin factura
                                </span>
                              )}
                              {p.factura_fecha
                                ? diasBadge(diasDesde(p.factura_fecha), `Emitida el ${String(p.factura_fecha).slice(0, 10).split('-').reverse().join('/')} - dias sin cobrar desde la emision`)
                                : p.fecha_evento && diasBadge(diasDesde(p.fecha_evento), 'Dias desde el evento (sin factura emitida)')}
                              {p.es_proforma && <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 mr-1">Pdte. factura</span>}
                              {p.factura_desactualizada && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-800 mr-1" title={`La factura no coincide con el presupuesto: revisa si hace falta rectificativa (presupuesto ${formatCurrency(p.factura_desactualizada.importe_presupuesto)}, facturado ${formatCurrency(p.factura_desactualizada.importe_facturado)})`}>
                                  Factura no coincide
                                </span>
                              )}
                              {p.no_quieren_factura
                                ? <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700 mr-1">No factura</span>
                                : p.sin_factura && <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-700 mr-1">Sin factura</span>}
                              {p.fecha_evento}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <div className="text-right">
                            <p className="text-sm font-bold text-red-600">{formatCurrency(p.pendiente)}</p>
                            <p className="text-xs text-slate-400">de {formatCurrency(p.importe_total)}</p>
                          </div>
                          <button
                            data-testid={`marcar-impagado-${p.numero}`}
                            onClick={(e) => { e.stopPropagation(); setShowImpagadoModal(p); setObsImpagado(''); }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Marcar como impagado"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {p.total_pagado > 0 && (
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (p.total_pagado / p.importe_total) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Impagados Tab */}
            {activeTab === 'impagados' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="impagados-panel">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Ban className="w-4 h-4 text-slate-600" />
                    Impagados
                  </h2>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      data-testid="search-impagados"
                      type="text"
                      placeholder="Buscar num o cliente..."
                      value={buscarImpagado}
                      onChange={(e) => setBuscarImpagado(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchImpagados()}
                      className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-56"
                    />
                  </div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {impagados.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <Ban className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No hay presupuestos marcados como impagados
                    </div>
                  ) : impagados.map(p => (
                    <ImpagadoItem
                      key={p.presupuesto_id}
                      item={p}
                      onDesmarcar={desmarcarImpagado}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Reclamaciones Tab */}
            {activeTab === 'reclamaciones' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="reclamaciones-panel">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    Reclamaciones de Cobro
                  </h2>
                  <button
                    data-testid="refresh-reclamaciones"
                    onClick={fetchReclamaciones}
                    disabled={loadingReclamaciones}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingReclamaciones ? 'animate-spin' : ''}`} />
                    Actualizar
                  </button>
                </div>
                <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
                  {loadingReclamaciones ? (
                    <div className="p-8 text-center text-slate-400">Cargando facturas...</div>
                  ) : reclamaciones.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No hay facturas pendientes de cobro
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100 bg-slate-50">
                          <th className="px-3 py-2 font-medium">Nº factura</th>
                          <th className="px-3 py-2 font-medium">Cliente</th>
                          <th className="px-3 py-2 font-medium">Vencimiento</th>
                          <th className="px-3 py-2 font-medium">Política</th>
                          <th className="px-3 py-2 font-medium text-right">Pendiente</th>
                          <th className="px-3 py-2 font-medium">Último aviso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reclamaciones.map(f => {
                          // Último aviso: SOLO recordatorios reales enviados al cliente
                          // (recordatorios_cobro). Se ignora el aviso interno a Telegram.
                          const recs = f.recordatorios_cobro || [];
                          const ua = recs.length > 0
                            ? recs.reduce((a, b) => (new Date(a.fecha) > new Date(b.fecha) ? a : b)).fecha
                            : null;
                          return (
                            <tr
                              key={f.factura_id}
                              data-testid={`reclamacion-${f.factura_id}`}
                              onClick={() => { setReclamacionSel(f); setDetalle(null); setSelectedPres(null); }}
                              className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${reclamacionSel?.factura_id === f.factura_id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                            >
                              <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{f.numero_factura || '-'}</td>
                              <td className="px-3 py-2 text-slate-600 max-w-[160px] truncate" title={f.cliente_nombre}>{f.cliente_nombre}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-slate-600">{f.vencimiento ? formatFecha(f.vencimiento) : '-'}</span>
                                  {!f.vencimiento
                                    ? emitidaBadge(f.dias_desde_emision)
                                    : f.vencida === false
                                      ? venceBadge(f.dias_vencida, formatFecha(f.vencimiento))
                                      : diasBadge(f.dias_vencida, `Vencida hace ${f.dias_vencida} dias`)}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {f.politica_configurada === false || !f.politica ? (
                                  <button
                                    data-testid={`politica-${f.factura_id}`}
                                    onClick={(e) => { e.stopPropagation(); setPoliticaFactura(f); }}
                                    className="text-xs text-gray-400 hover:text-duetto-primary"
                                    title="Configurar política de pago del cliente"
                                  >
                                    Configurar
                                  </button>
                                ) : (
                                  <button
                                    data-testid={`politica-${f.factura_id}`}
                                    onClick={(e) => { e.stopPropagation(); setPoliticaFactura(f); }}
                                    className="flex items-center gap-1.5 text-left hover:underline"
                                    title="Configurar política de pago del cliente"
                                  >
                                    {f.via_face ? (
                                      <span
                                        className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 text-purple-800"
                                        title="Administración pública: sin aviso Telegram; aparece aquí a partir de 90 días"
                                      >
                                        FACe
                                      </span>
                                    ) : (
                                      <span className="text-slate-600 text-xs">{f.politica}</span>
                                    )}
                                    {f.via_face && f.politica && f.politica !== 'FACe' && (
                                      <span className="text-slate-400 text-xs">{f.politica}</span>
                                    )}
                                  </button>
                                )}
                              </td>
                              <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${!f.vencida && f.vencimiento ? 'text-slate-600' : 'text-red-600'}`}>{formatCurrency(f.importe_pendiente)}</td>
                              <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{ua ? formatFechaHora(ua) : '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Cobrados Tab */}
            {activeTab === 'cobrados' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cobrados-panel">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Cobrados ({cobrados.length})
                  </h2>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      data-testid="search-cobrados"
                      type="text"
                      placeholder="Buscar num o cliente..."
                      value={buscarCobrado}
                      onChange={(e) => setBuscarCobrado(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchCobrados()}
                      className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-56"
                    />
                  </div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {cobrados.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No hay presupuestos cobrados
                    </div>
                  ) : cobrados.map(p => (
                    <div
                      key={p.presupuesto_id}
                      onClick={() => { setFilaSeleccionada(null); fetchDetalle(p.presupuesto_id); }}
                      className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${selectedPres === p.presupuesto_id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      data-testid={`cobrado-${p.presupuesto_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <a
                              href={`/presupuestos/${p.presupuesto_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="font-bold text-emerald-700 text-sm hover:text-blue-600 hover:underline"
                              title="Abrir presupuesto en otra pestaña"
                            >
                              #{p.numero}
                            </a>
                            <span className="text-sm text-slate-700 truncate">{p.cliente_nombre}</span>
                            {p.factura_numero && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {p.factura_numero}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>Total: <span className="font-medium text-slate-700">{formatCurrency(p.importe_total)}</span></span>
                            <span>Pagado: <span className="font-medium text-emerald-600">{formatCurrency(p.total_pagado)}</span></span>
                            {p.fecha_evento && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.fecha_evento}</span>}
                          </div>
                        </div>
                        <button
                          data-testid={`desmarcar-pagado-${p.presupuesto_id}`}
                          onClick={(e) => { e.stopPropagation(); desmarcarPagado(p.presupuesto_id); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 transition-colors"
                          title="Desmarcar como pagado y recalcular estado"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Desmarcar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right panel - Detail / Ingresos */}
          <div className="lg:col-span-2 space-y-4">
            {detalle && (
              <div className="bg-white rounded-xl border border-slate-200" data-testid="detalle-presupuesto">
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-700">#{detalle.presupuesto.numero} - {detalle.presupuesto.cliente}</h3>
                    <button onClick={() => { setDetalle(null); setSelectedPres(null); }} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <div>
                      <span className="text-xs text-slate-500">Total: </span>
                      <span className="text-sm font-bold">{formatCurrency(detalle.presupuesto.importe_total)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Pagado: </span>
                      <span className="text-sm font-bold text-emerald-600">{formatCurrency(detalle.presupuesto.total_pagado)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Pendiente: </span>
                      <span className="text-sm font-bold text-red-600">{formatCurrency(detalle.presupuesto.pendiente)}</span>
                    </div>
                  </div>
                  {detalle.presupuesto.factura_desactualizada && (
                    <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="aviso-factura-desactualizada">
                      La factura no coincide con el presupuesto: revisa si hace falta rectificativa
                      {' '}(presupuesto {formatCurrency(detalle.presupuesto.factura_desactualizada.importe_presupuesto)}, facturado {formatCurrency(detalle.presupuesto.factura_desactualizada.importe_facturado)}). El pendiente se calcula sobre lo facturado.
                    </p>
                  )}
                </div>
                {detalle.facturas.length > 0 && (
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-1">Facturas</p>
                    {detalle.facturas.map(f => (
                      <div key={f.id} className="py-1">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3 h-3 text-blue-500" />
                          <span className="text-xs font-medium">{f.numero_factura}</span>
                          <span className="text-xs text-slate-400">{f.tipo_factura}</span>
                          {estadoBadge(f.estado_pago)}
                        </div>
                        {(f.envios || []).length > 0 ? (
                          <p className="text-[10px] text-slate-400 pl-5">
                            Enviada el {formatFechaHora(f.envios[f.envios.length - 1].fecha)} a {f.envios[f.envios.length - 1].destinatario}
                            {f.envios.length > 1 ? ` (+${f.envios.length - 1} envíos)` : ''}
                          </p>
                        ) : (
                          <p className="text-[10px] text-slate-300 pl-5">Sin envío por email registrado</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-4 py-2">
                  <p className="text-xs font-medium text-slate-500 mb-1">Pagos vinculados</p>
                  {detalle.vinculos.length === 0 && detalle.pagos_manuales.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">Sin pagos registrados</p>
                  ) : (
                    <div className="space-y-1.5">
                      {detalle.vinculos.map(v => (
                        <div key={v.id} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-emerald-700">{formatCurrency(v.importe_asignado)}</p>
                            <p className="text-[10px] text-emerald-600 truncate">{v.cargo?.concepto || v.movimiento_id?.substring(0, 30)}</p>
                            <p className="text-[10px] text-slate-400">{v.cargo?.fecha || ''}</p>
                          </div>
                          <button
                            data-testid={`desvincular-${v.id}`}
                            onClick={(e) => { e.stopPropagation(); desvincular(v.id); }}
                            className="text-red-400 hover:text-red-600 p-1"
                            title="Desvincular"
                          >
                            <Unlink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      {detalle.pagos_manuales.map((pm, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs font-medium text-slate-600">{formatCurrency(pm.monto || pm.cantidad)}</p>
                            <p className="text-[10px] text-slate-400">Pago manual - {pm.metodo || pm.tipo || 'Registro antiguo'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {detalle.presupuesto.pendiente > 0.01 && (
                    <>
                      <button
                        data-testid="vincular-ingreso-btn"
                        onClick={() => {
                          // Fila de una factura de jornada: el ingreso va a esa factura
                          const fila = filaSeleccionada && filaSeleccionada.presupuesto_id === detalle.presupuesto.id && filaSeleccionada.factura_id
                            ? filaSeleccionada : null;
                          setVinculandoIngreso(fila
                            ? { ...detalle.presupuesto, factura_id: fila.factura_id, factura_numero: fila.factura_numero, pendiente: fila.pendiente }
                            : detalle.presupuesto);
                          setShowIngresos(true);
                        }}
                        className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        <Link2 className="w-4 h-4" />
                        {filaSeleccionada && filaSeleccionada.presupuesto_id === detalle.presupuesto.id && filaSeleccionada.factura_id
                          ? `Vincular Ingreso a ${filaSeleccionada.factura_numero || 'la factura de la jornada'}`
                          : 'Vincular Ingreso Bancario'}
                      </button>
                      <button
                        data-testid="marcar-pagado-manual-btn"
                        onClick={async () => {
                          if (!window.confirm(`¿Marcar presupuesto #${detalle.presupuesto.numero} como PAGADO manualmente?\n\nTotal: ${formatCurrency(detalle.presupuesto.importe_total)}\n\nEsto no requiere vincular ningún ingreso bancario.`)) return;
                          try {
                            await fetcher('/api/cobros-unificado/marcar-pagado-manual', {
                              method: 'POST',
                              body: JSON.stringify({ presupuesto_id: detalle.presupuesto.id, notas: 'Marcado como pagado manualmente desde Control de Cobros' })
                            });
                            fetchResumen(); fetchPendientes(); fetchCobrados();
                            fetchDetalle(detalle.presupuesto.id);
                          } catch (e) { alert(e.message); }
                        }}
                        className="w-full mt-1.5 flex items-center justify-center gap-2 px-3 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Marcar como Pagado
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {showIngresos && vinculandoIngreso && (
              <div className="bg-white rounded-xl border border-blue-200" data-testid="ingresos-panel">
                <div className="px-4 py-3 border-b border-blue-100 bg-blue-50 rounded-t-xl">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-blue-800 text-sm">
                      Ingresos disponibles
                      <span className="text-xs text-blue-600 ml-2">
                        Vincular a #{vinculandoIngreso.numero}{vinculandoIngreso.factura_numero ? `, factura ${vinculandoIngreso.factura_numero}` : ''} ({formatCurrency(vinculandoIngreso.pendiente)} pend.)
                      </span>
                    </h3>
                    <button onClick={() => { setShowIngresos(false); setVinculandoIngreso(null); }} className="text-blue-400 hover:text-blue-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="relative mt-2">
                    <Search className="w-4 h-4 absolute left-2.5 top-2 text-blue-400" />
                    <input
                      data-testid="search-ingresos"
                      type="text"
                      placeholder="Buscar concepto..."
                      value={buscarIngreso}
                      onChange={(e) => setBuscarIngreso(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchIngresos()}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-blue-200 rounded-lg bg-white"
                    />
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      data-testid="toggle-vinculados"
                      type="checkbox"
                      checked={incluirVinculados}
                      onChange={(e) => setIncluirVinculados(e.target.checked)}
                      className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-blue-700">Incluir ya vinculados</span>
                  </label>
                </div>
                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {ingresos.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">No hay ingresos sin vincular</div>
                  ) : ingresos.map(ing => (
                    <div key={ing.id} className={`px-4 py-2.5 transition-colors ${ing.ya_vinculado ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-blue-50'}`} data-testid={`ingreso-${ing.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-700 truncate">{ing.concepto}</p>
                            {ing.ya_vinculado && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-200 text-amber-800 flex-shrink-0">
                                {ing.vinculado_parcial ? 'Vinculado en parte' : 'Vinculado'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">
                            {ing.fecha}
                            {ing.vinculado_parcial && <span className="ml-2 text-amber-700">Disponible {formatCurrency(ing.disponible)} de {formatCurrency(ing.importe)}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <span className="text-sm font-bold text-emerald-600">{formatCurrency(ing.vinculado_parcial ? ing.disponible : ing.importe)}</span>
                          {(!ing.ya_vinculado || ing.vinculado_parcial) && (
                            <button
                              data-testid={`vincular-${ing.id}`}
                              onClick={() => vincular(ing.id, vinculandoIngreso.id, ing.disponible ?? ing.importe, vinculandoIngreso.factura_id)}
                              className="px-2.5 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium flex items-center gap-1"
                            >
                              <Link2 className="w-3 h-3" />
                              Vincular
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detalle de reclamacion (al seleccionar una fila de Reclamaciones) */}
            {activeTab === 'reclamaciones' && reclamacionSel && (
              <ReclamacionDetalle
                factura={reclamacionSel}
                formatCurrency={formatCurrency}
                onClose={() => setReclamacionSel(null)}
                onConfigurarPolitica={() => setPoliticaFactura(reclamacionSel)}
                onAviso={mostrarAviso}
                onRefrescar={fetchReclamaciones}
              />
            )}

            {!detalle && !showIngresos && !(activeTab === 'reclamaciones' && reclamacionSel) && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <CreditCard className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="text-sm text-slate-400 mt-3">
                  {activeTab === 'reclamaciones'
                    ? 'Selecciona una factura vencida para ver el detalle y enviar un recordatorio'
                    : 'Selecciona un presupuesto pendiente para ver el detalle y vincular pagos'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal vincular ingreso a multiples presupuestos */}
      {multiLinkIngreso && (
        <MultiLinkModal
          ingreso={multiLinkIngreso}
          pendientes={multiLinkPendientes}
          selections={multiLinkSelections}
          setSelections={setMultiLinkSelections}
          searchQuery={multiLinkSearch}
          setSearchQuery={setMultiLinkSearch}
          formatCurrency={formatCurrency}
          onClose={() => { setMultiLinkIngreso(null); setMultiLinkSelections([]); }}
          onVincular={async (ingresoId, vinculos) => {
            try {
              let result;
              if (vinculos.length === 1) {
                result = await fetcher('/api/cobros-unificado/vincular', {
                  method: 'POST',
                  body: JSON.stringify({
                    presupuesto_id: vinculos[0].presupuesto_id,
                    ingreso_id: ingresoId,
                    importe: vinculos[0].importe,
                    // Multievento por jornada: a la factura de esa jornada
                    ...(vinculos[0].factura_id ? { factura_id: vinculos[0].factura_id } : {}),
                  })
                });
              } else {
                result = await fetcher('/api/cobros-unificado/vincular-multiple', {
                  method: 'POST',
                  body: JSON.stringify({
                    ingreso_id: ingresoId,
                    vinculos: vinculos.map(v => ({
                      presupuesto_id: v.presupuesto_id,
                      importe: v.importe,
                      ...(v.factura_id ? { factura_id: v.factura_id } : {}),
                    })),
                  })
                });
              }
              setMultiLinkIngreso(null);
              setMultiLinkSelections([]);
              fetchResumen(); fetchPendientes(); fetchIngresos();
              if (activeTab === 'cobrados') fetchCobrados();
              // Check for excess payment
              if (result?.exceso && result.exceso.importe > 0.01) {
                setExcesoData(result.exceso);
              }
            } catch (e) { alert(e.message); }
          }}
        />
      )}

      {/* Modal gestión de exceso de pago */}
      {excesoData && (
        <ExcesoModal
          exceso={excesoData}
          formatCurrency={formatCurrency}
          onClose={() => setExcesoData(null)}
          onDone={() => {
            setExcesoData(null);
            fetchResumen(); fetchPendientes(); fetchIngresos();
            if (selectedPres) fetchDetalle(selectedPres);
          }}
        />
      )}

      {/* Modal marcar como impagado */}
      {showImpagadoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowImpagadoModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="modal-impagado">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Marcar como impagado</h3>
                <p className="text-sm text-slate-500">#{showImpagadoModal.numero} - {showImpagadoModal.cliente_nombre}</p>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-slate-600 mb-1">Importe pendiente: <span className="font-bold text-red-600">{formatCurrency(showImpagadoModal.pendiente)}</span></p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Observaciones</label>
              <textarea
                data-testid="observaciones-impagado-input"
                value={obsImpagado}
                onChange={(e) => setObsImpagado(e.target.value)}
                placeholder="Motivos del impago, decisiones tomadas, acciones futuras..."
                className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none h-28 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowImpagadoModal(null)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                data-testid="confirmar-impagado-btn"
                onClick={() => marcarImpagado(showImpagadoModal.presupuesto_id, obsImpagado)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                Confirmar impagado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal politica de pago del cliente */}
      {politicaFactura && (
        <PoliticaPagoModal
          factura={politicaFactura}
          onClose={() => setPoliticaFactura(null)}
          onGuardado={(msg) => {
            setPoliticaFactura(null);
            mostrarAviso('ok', msg || 'Política de pago actualizada');
            fetchReclamaciones();
          }}
          onError={(msg) => mostrarAviso('error', msg)}
        />
      )}
    </div>
  );
}

// Modal para vincular un ingreso a uno o multiples presupuestos
function MultiLinkModal({ ingreso, pendientes, selections, setSelections, searchQuery, setSearchQuery, formatCurrency, onClose, onVincular }) {
  const totalAsignado = selections.reduce((sum, s) => sum + (s.importe || 0), 0);
  // Lo que queda por vincular del ingreso (un vínculo parcial no lo agota)
  const disponibleIngreso = typeof ingreso.disponible === 'number' ? ingreso.disponible : (ingreso.importe || 0);
  const restante = Math.max(0, disponibleIngreso - totalAsignado);
  const excedeIngreso = totalAsignado > disponibleIngreso + 0.01;
  const excedePendiente = selections.some(s => (s.importe || 0) > (s.pendiente || 0) + 0.01);

  // Multievento por jornada: cada factura de jornada es una fila propia
  const claveFila = (p) => p.fila_id || p.presupuesto_id;
  const filteredPendientes = pendientes.filter(p => {
    if (selections.some(s => s.clave === claveFila(p))) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    // Busqueda por importe: acepta coma o punto decimal, tolerancia 1 EUR.
    // Casa contra el pendiente, el total del evento o su mitad (anticipo 50%).
    if (/^\d+([.,]\d{1,2})?$/.test(q)) {
      const qNum = parseFloat(q.replace(',', '.'));
      const tol = 1;
      if (Math.abs((p.pendiente || 0) - qNum) <= tol) return true;
      if (Math.abs((p.importe_total || 0) - qNum) <= tol) return true;
      if (Math.abs((p.importe_total || 0) / 2 - qNum) <= tol) return true;
    }
    return (p.cliente_nombre || '').toLowerCase().includes(q) || String(p.numero).includes(q) || (p.factura_numero || '').toLowerCase().includes(q);
  });

  const addSelection = (p) => {
    const importeDefault = Math.min(p.pendiente, restante);
    setSelections([...selections, {
      clave: claveFila(p),
      presupuesto_id: p.presupuesto_id,
      factura_id: p.factura_id || null,
      factura_numero: p.factura_numero,
      numero: p.numero,
      cliente_nombre: p.cliente_nombre,
      pendiente: p.pendiente,
      importe: Math.round(importeDefault * 100) / 100,
    }]);
  };

  const updateImporte = (idx, val) => {
    const updated = [...selections];
    updated[idx] = { ...updated[idx], importe: parseFloat(val) || 0 };
    setSelections(updated);
  };

  const removeSelection = (idx) => {
    setSelections(selections.filter((_, i) => i !== idx));
  };

  const canSubmit = selections.length > 0 && totalAsignado > 0 && !excedeIngreso;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="multi-link-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Split className="w-5 h-5 text-blue-600" />
              Asignar Ingreso a Presupuesto(s)
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm text-slate-700 truncate" title={ingreso.concepto}>{ingreso.concepto}</p>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-lg font-bold text-blue-700">{formatCurrency(ingreso.importe)}</span>
              <span className="text-xs text-slate-400">{ingreso.fecha}</span>
            </div>
          </div>
        </div>

        {/* Selections */}
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-medium text-slate-500 mb-2">Presupuestos seleccionados ({selections.length})</p>
          {selections.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">Selecciona presupuestos de la lista inferior</p>
          ) : (
            <div className="space-y-2">
              {selections.map((s, i) => (
                <div key={s.clave || s.presupuesto_id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200" data-testid={`multi-selection-${s.numero}`}>
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-bold text-slate-600">#{s.numero}</span>
                    {s.factura_id && s.factura_numero && <span className="text-xs text-slate-500 ml-2">{s.factura_numero}</span>}
                    <span className="text-sm text-slate-500 ml-2 truncate">{s.cliente_nombre}</span>
                    <span className="text-xs text-slate-400 ml-2">(pend: {formatCurrency(s.pendiente)})</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      data-testid={`multi-importe-${s.numero}`}
                      type="number"
                      step="0.01"
                      min="0"
                      max={Math.min(s.pendiente, disponibleIngreso)}
                      value={s.importe}
                      onChange={(e) => updateImporte(i, e.target.value)}
                      className="w-24 px-2 py-1 text-sm border border-slate-200 rounded text-right font-medium"
                    />
                    <span className="text-xs text-slate-400">EUR</span>
                    <button
                      data-testid={`multi-remove-${s.numero}`}
                      onClick={() => removeSelection(i)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-slate-500">
                  Asignado: <span className={`font-bold ${excedeIngreso ? 'text-red-600' : 'text-blue-700'}`}>{formatCurrency(totalAsignado)}</span>
                  {' / '}{formatCurrency(disponibleIngreso)}
                </span>
                {restante > 0.01 && <span className="text-xs text-amber-600 font-medium">Restante: {formatCurrency(restante)}</span>}
              </div>
              {excedeIngreso && (
                <p className="text-xs text-red-600" data-testid="multi-aviso-excede">No cuadra: lo asignado supera lo disponible del ingreso ({formatCurrency(disponibleIngreso)}).</p>
              )}
              {!excedeIngreso && excedePendiente && (
                <p className="text-xs text-amber-700" data-testid="multi-aviso-pendiente">No cuadra: algún presupuesto recibe más de lo que tiene pendiente; lo que sobre quedará como exceso.</p>
              )}
            </div>
          )}
        </div>

        {/* Pendientes list */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div className="px-5 py-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
              <input
                data-testid="multi-link-search"
                type="text"
                placeholder="Buscar por cliente, numero, factura o importe (ej. 649)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {filteredPendientes.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No hay presupuestos pendientes disponibles</div>
            ) : filteredPendientes.map(p => (
              <div key={claveFila(p)} className={`px-5 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors ${p.match_puntos >= 60 ? 'bg-emerald-50/60' : ''}`} data-testid={`multi-pendiente-${p.numero}${p.factura_id ? `-${p.factura_id}` : ''}`} onClick={() => addSelection(p)}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-600">#{p.numero}</span>
                      <span className="text-sm text-slate-700 truncate">{p.cliente_nombre}</span>
                      {p.match_puntos >= 60 && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 flex-shrink-0" title={(p.match_razones || []).join(' · ')}>
                          Sugerido
                        </span>
                      )}
                      {p.evento_futuro && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 flex-shrink-0" title={`Evento el ${p.fecha_evento}`}>
                          Evento futuro
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {p.factura_numero && <span className="mr-2">Factura: {p.factura_numero}</span>}
                      {p.es_factura_jornada && p.jornada_fecha && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-sky-100 text-sky-700 mr-2">
                          Jornada {String(p.jornada_fecha).slice(0, 10).split('-').reverse().join('/')}
                        </span>
                      )}
                      {p.es_resto_jornadas && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-sky-100 text-sky-700 mr-2">Jornadas sin factura</span>
                      )}
                      {p.no_quieren_factura
                        ? <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700 mr-2">No factura</span>
                        : p.sin_factura && <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-700 mr-2">Sin factura</span>}
                      Pendiente: <span className="font-medium text-red-600">{formatCurrency(p.pendiente)}</span>
                    </p>
                    {p.match_puntos >= 60 && (p.match_razones || []).length > 0 && (
                      <p className="text-[11px] text-emerald-600 mt-0.5">{p.match_razones.join(' · ')}</p>
                    )}
                  </div>
                  <Plus className="w-4 h-4 text-blue-500 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
          <button
            data-testid="multi-link-confirm-btn"
            onClick={() => onVincular(ingreso.id, selections)}
            disabled={!canSubmit}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Link2 className="w-4 h-4" />
            Vincular {formatCurrency(totalAsignado)} a {selections.length} presupuesto{selections.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para gestionar exceso de pago (devolución o depósito)
function ExcesoModal({ exceso, formatCurrency, onClose, onDone }) {
  const [mode, setMode] = useState(null); // null | 'devolucion' | 'deposito'
  const [buscarCargo, setBuscarCargo] = useState('');
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notasDeposito, setNotasDeposito] = useState('');

  const fetchCargos = async (q) => {
    setLoadingCargos(true);
    try {
      const params = q ? `?buscar=${encodeURIComponent(q)}` : '';
      const data = await fetcher(`/api/cobros-unificado/cargos-disponibles${params}`);
      setCargos(data || []);
    } catch (e) { console.error(e); }
    setLoadingCargos(false);
  };

  useEffect(() => {
    if (mode === 'devolucion') fetchCargos('');
  }, [mode]);

  const handleDevolucion = async (cargoId, importeCargo) => {
    setProcessing(true);
    try {
      const importeDevolucion = Math.min(exceso.importe, importeCargo);
      await fetcher('/api/cobros-unificado/vincular-devolucion-exceso', {
        method: 'POST',
        body: JSON.stringify({
          presupuesto_id: exceso.presupuesto_id,
          cargo_id: cargoId,
          importe: importeDevolucion,
          notas: `Devolución exceso presupuesto #${exceso.presupuesto_numero}`
        })
      });
      onDone();
    } catch (e) { alert(e.message); }
    setProcessing(false);
  };

  const handleDeposito = async () => {
    setProcessing(true);
    try {
      await fetcher('/api/cobros-unificado/marcar-deposito', {
        method: 'POST',
        body: JSON.stringify({
          presupuesto_id: exceso.presupuesto_id,
          importe: exceso.importe,
          notas: notasDeposito || `Exceso de pago presupuesto #${exceso.presupuesto_numero}`
        })
      });
      onDone();
    } catch (e) { alert(e.message); }
    setProcessing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="exceso-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" />
              Exceso de Pago Detectado
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="exceso-close-btn"><X className="w-5 h-5" /></button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-slate-700">
              El pago del presupuesto <span className="font-bold">#{exceso.presupuesto_numero}</span>
              {exceso.cliente_nombre && <span> ({exceso.cliente_nombre})</span>} excede en{' '}
              <span className="font-bold text-amber-700">{formatCurrency(exceso.importe)}</span> el total de la factura.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!mode && (
            <div className="space-y-3" data-testid="exceso-options">
              <p className="text-sm text-slate-600 mb-4">¿Qué deseas hacer con el exceso?</p>
              <button
                data-testid="exceso-opcion-devolucion"
                onClick={() => setMode('devolucion')}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="p-2.5 bg-blue-100 rounded-lg flex-shrink-0">
                  <RotateCcw className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Registrar Devolución</p>
                  <p className="text-xs text-slate-500 mt-0.5">Vincular a un cargo bancario (transferencia de devolución al cliente)</p>
                </div>
              </button>
              <button
                data-testid="exceso-opcion-deposito"
                onClick={() => setMode('deposito')}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left"
              >
                <div className="p-2.5 bg-emerald-100 rounded-lg flex-shrink-0">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Guardar como Depósito</p>
                  <p className="text-xs text-slate-500 mt-0.5">Mantener el exceso a cuenta del cliente para futuros presupuestos</p>
                </div>
              </button>
              <button
                data-testid="exceso-ignorar"
                onClick={onClose}
                className="w-full text-center text-xs text-slate-400 hover:text-slate-600 py-2"
              >
                Gestionar más tarde
              </button>
            </div>
          )}

          {mode === 'devolucion' && (
            <div data-testid="exceso-devolucion-view">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600" data-testid="exceso-back-btn">
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  <h4 className="font-semibold text-slate-700 text-sm">Seleccionar cargo de devolución</h4>
                </div>
                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">{formatCurrency(exceso.importe)}</span>
              </div>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <input
                  data-testid="exceso-buscar-cargo"
                  type="text"
                  placeholder="Buscar cargo bancario..."
                  value={buscarCargo}
                  onChange={(e) => setBuscarCargo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchCargos(buscarCargo)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg"
                />
              </div>
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {loadingCargos ? (
                  <p className="text-center text-sm text-slate-400 py-4">Buscando cargos...</p>
                ) : cargos.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-4">No hay cargos disponibles</p>
                ) : cargos.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-slate-50 hover:bg-blue-50 rounded-lg px-3 py-2.5 transition-colors" data-testid={`cargo-devolucion-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-700 truncate">{c.concepto}</p>
                      <p className="text-xs text-slate-400">{c.fecha}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="text-sm font-bold text-red-600">{formatCurrency(c.importe)}</span>
                      <button
                        data-testid={`vincular-devolucion-${c.id}`}
                        onClick={() => handleDevolucion(c.id, c.importe)}
                        disabled={processing}
                        className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50"
                      >
                        {processing ? 'Vinculando...' : 'Vincular'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === 'deposito' && (
            <div data-testid="exceso-deposito-view">
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600" data-testid="exceso-back-btn-dep">
                  <ArrowRight className="w-4 h-4 rotate-180" />
                </button>
                <h4 className="font-semibold text-slate-700 text-sm">Guardar como depósito</h4>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-700">
                  Se registrará un depósito de <span className="font-bold text-emerald-700">{formatCurrency(exceso.importe)}</span> a cuenta de{' '}
                  <span className="font-bold">{exceso.cliente_nombre}</span>.
                </p>
                <p className="text-xs text-slate-500 mt-1">Este importe se podrá aplicar a futuros presupuestos de este cliente.</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas (opcional)</label>
                <textarea
                  data-testid="exceso-deposito-notas"
                  value={notasDeposito}
                  onChange={(e) => setNotasDeposito(e.target.value)}
                  placeholder="Notas sobre este depósito..."
                  className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                />
              </div>
              <button
                data-testid="confirmar-deposito-btn"
                onClick={handleDeposito}
                disabled={processing}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
              >
                <Banknote className="w-4 h-4" />
                {processing ? 'Registrando...' : `Registrar depósito de ${formatCurrency(exceso.importe)}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// Componente separado para impagados con auto-guardado de observaciones
function ImpagadoItem({ item, onDesmarcar, formatCurrency }) {
  const [obs, setObs] = useState(item.observaciones_impago || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);

  const saveObservaciones = useCallback(async (text) => {
    setSaving(true);
    try {
      await fetcher(`/api/cobros-unificado/impagado/${item.presupuesto_id}/observaciones`, {
        method: 'PUT',
        body: JSON.stringify({ observaciones: text })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [item.presupuesto_id]);

  const handleChange = (e) => {
    const text = e.target.value;
    setObs(text);
    setSaved(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveObservaciones(text), 1000);
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div className="px-4 py-3" data-testid={`impagado-${item.numero}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-bold text-slate-600 flex-shrink-0">#{item.numero}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{item.cliente_nombre}</p>
            <p className="text-xs text-slate-400">
              {item.factura_numero && <span className="mr-2">Factura: {item.factura_numero}</span>}
              {item.no_quieren_factura
                ? <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700 mr-1">No factura</span>
                : item.sin_factura && <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-700 mr-1">Sin factura</span>}
              {item.fecha_evento}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <div className="text-right">
            <p className="text-sm font-bold text-red-600">{formatCurrency(item.pendiente)}</p>
            <p className="text-xs text-slate-400">de {formatCurrency(item.importe_total)}</p>
          </div>
          <button
            data-testid={`desmarcar-impagado-${item.numero}`}
            onClick={() => onDesmarcar(item.presupuesto_id)}
            className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="Devolver a pendiente"
          >
            <Undo2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {item.fecha_impagado && (
        <p className="text-[10px] text-slate-400 mb-1.5">
          Marcado el {new Date(item.fecha_impagado).toLocaleDateString('es-ES')}
          {item.marcado_impagado_por && ` por ${item.marcado_impagado_por}`}
        </p>
      )}
      <div className="relative">
        <textarea
          data-testid={`observaciones-${item.numero}`}
          value={obs}
          onChange={handleChange}
          placeholder="Escribe las observaciones sobre este impago..."
          className="w-full border border-slate-200 rounded-lg p-2.5 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
        />
        <div className="absolute bottom-2 right-2">
          {saving && <span className="text-[10px] text-slate-400">Guardando...</span>}
          {saved && <span className="text-[10px] text-emerald-500 flex items-center gap-0.5"><Check className="w-3 h-3" /> Guardado</span>}
        </div>
      </div>
    </div>
  );
}

// Panel lateral de detalle de una reclamacion (factura vencida).
// Muestra datos, formulario de envio de recordatorio (con CC), historial de
// recordatorios enviados y observaciones. Reemplaza a los antiguos modales
// ReclamarModal e HistorialReclamacionModal.
function ReclamacionDetalle({ factura, formatCurrency, onClose, onConfigurarPolitica, onAviso, onRefrescar }) {
  const [mostrarEnvio, setMostrarEnvio] = useState(false);

  // Datos del propio item de la lista (ya trae recordatorios_cobro y observaciones_cobro).
  const recordatorios = factura.recordatorios_cobro || [];
  const observaciones = factura.observaciones_cobro || [];

  // Al cambiar de factura seleccionada, colapsar el formulario de envio.
  useEffect(() => { setMostrarEnvio(false); }, [factura.factura_id]);

  const viaLabel = (r) => {
    const v = (r.via || '').toLowerCase();
    if (v === 'telegram' || v === 'telegram_reclamacion') return 'Telegram';
    return 'ERP';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="reclamacion-detalle">
      {/* Cabecera */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-700 truncate">{factura.numero_factura || '-'}</h3>
          <p className="text-xs text-slate-500 truncate">{factura.cliente_nombre}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0" data-testid="reclamacion-detalle-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {mostrarEnvio ? (
        <ReclamacionEnvioForm
          factura={factura}
          formatCurrency={formatCurrency}
          onVolver={() => setMostrarEnvio(false)}
          onAviso={onAviso}
          onEnviado={() => { setMostrarEnvio(false); onRefrescar(); }}
        />
      ) : (
        <>
          {/* Datos */}
          <div className="px-4 py-3 border-b border-slate-100 space-y-1.5 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">Fecha emisión</span>
              <span className="text-slate-700">{factura.fecha_emision ? formatFecha(factura.fecha_emision) : '-'}</span>
            </div>
            {factura.fecha_evento && (
              <div className="flex justify-between gap-2">
                <span className="text-xs text-slate-500">Fecha evento</span>
                <span className="text-slate-700 flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" />{formatFecha(factura.fecha_evento)}</span>
              </div>
            )}
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">Importe total</span>
              <span className="text-slate-700">{formatCurrency(factura.importe_total)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">Pagado</span>
              <span className="text-emerald-600">{formatCurrency((factura.importe_total || 0) - (factura.importe_pendiente || 0))}</span>
            </div>
            <div className="flex justify-between gap-2 pt-1 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-600">Pendiente</span>
              <span className="text-base font-bold text-red-600">{formatCurrency(factura.importe_pendiente)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">{!factura.vencimiento ? 'Vencimiento' : factura.vencida === false ? 'Vence el' : 'Vencida desde'}</span>
              <span className="text-slate-700 flex items-center gap-1.5">
                {factura.vencimiento ? formatFecha(factura.vencimiento) : '-'}
                {!factura.vencimiento
                  ? emitidaBadge(factura.dias_desde_emision)
                  : factura.vencida === false
                    ? venceBadge(factura.dias_vencida, formatFecha(factura.vencimiento))
                    : diasBadge(factura.dias_vencida, `Vencida hace ${factura.dias_vencida} dias`)}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">Factura enviada</span>
              {(factura.envios_factura || []).length > 0 ? (
                <span className="text-slate-700 text-xs text-right">
                  {formatFechaHora(factura.envios_factura[factura.envios_factura.length - 1].fecha)}
                  <span className="text-slate-500"> a {factura.envios_factura[factura.envios_factura.length - 1].destinatario}</span>
                  {factura.envios_factura.length > 1 && (
                    <span className="text-slate-400"> (+{factura.envios_factura.length - 1})</span>
                  )}
                </span>
              ) : (
                <span className="text-slate-400 text-xs">Sin envío registrado</span>
              )}
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-xs text-slate-500">Política</span>
              <button
                onClick={onConfigurarPolitica}
                className="text-xs text-gray-400 hover:text-duetto-primary"
                data-testid="reclamacion-detalle-politica"
              >
                {factura.politica_configurada && factura.politica ? factura.politica : 'Configurar'}
              </button>
            </div>
          </div>

          {/* Boton primario: enviar recordatorio */}
          <div className="px-4 py-3 border-b border-slate-100">
            <button
              data-testid="reclamacion-abrir-envio"
              onClick={() => setMostrarEnvio(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium"
            >
              <Send className="w-4 h-4" />
              Enviar recordatorio
            </button>
          </div>

          {/* Recordatorios enviados */}
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Recordatorios enviados</p>
            {recordatorios.length === 0 ? (
              <p className="text-sm text-slate-400 py-1">Sin recordatorios todavía.</p>
            ) : (
              <div className="space-y-2">
                {recordatorios.map((r, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`recordatorio-item-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-700">{formatFechaHora(r.fecha)}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${viaLabel(r) === 'Telegram' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {viaLabel(r)}{r.nivel ? ` · Aviso ${r.nivel}` : ''}
                      </span>
                    </div>
                    {r.destinatario && <p className="text-xs text-slate-500 mt-0.5">Para: {r.destinatario}</p>}
                    {r.asunto && <p className="text-xs text-slate-500 mt-0.5 truncate" title={r.asunto}>{r.asunto}</p>}
                    {Array.isArray(r.cc) && r.cc.length > 0 && (
                      <p className="text-[10px] text-slate-400 mt-0.5 truncate" title={r.cc.join(', ')}>CC: {r.cc.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Observaciones */}
          <ReclamacionObservaciones
            facturaId={factura.factura_id}
            observaciones={observaciones}
            onAviso={onAviso}
            onGuardado={onRefrescar}
          />
        </>
      )}
    </div>
  );
}

// Formulario de envio de recordatorio embebido en el panel de detalle.
function ReclamacionEnvioForm({ factura, formatCurrency, onVolver, onAviso, onEnviado }) {
  const [nivel, setNivel] = useState(1);
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [emailDestino, setEmailDestino] = useState('');
  const [nombreAdjunto, setNombreAdjunto] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [editado, setEditado] = useState(false);

  // CC: contactos del cliente + copia interna + email libre.
  const [contactos, setContactos] = useState([]);
  const [ccSeleccionados, setCcSeleccionados] = useState([]); // emails de contactos marcados
  const [copiaInterna, setCopiaInterna] = useState(false);
  const [ccLibre, setCcLibre] = useState('');

  const cargarPlantilla = useCallback(async (nivelSolicitado) => {
    setLoading(true);
    try {
      const res = await cobrosReclamacionAPI.plantilla(factura.factura_id, nivelSolicitado);
      const d = res.data || {};
      setAsunto(d.asunto || '');
      setMensaje(d.mensaje || '');
      setEmailDestino(d.email_destino || factura.email_destino_sugerido || '');
      setNombreAdjunto(d.nombre_adjunto || '');
      if (d.nivel) setNivel(d.nivel);
      setEditado(false);
    } catch (e) {
      onAviso('error', detalleError(e, 'No se pudo cargar la plantilla'));
    }
    setLoading(false);
  }, [factura.factura_id, factura.email_destino_sugerido, onAviso]);

  useEffect(() => {
    cargarPlantilla(factura.nivel || 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contactos del cliente para el selector de CC.
  useEffect(() => {
    let activo = true;
    if (!factura.cliente_id) { setContactos([]); return; }
    cobrosReclamacionAPI.contactos(factura.cliente_id)
      .then(res => { if (activo) setContactos(res.data?.contactos || []); })
      .catch(() => { if (activo) setContactos([]); });
    return () => { activo = false; };
  }, [factura.cliente_id]);

  const cambiarNivel = (nuevoNivel) => {
    if (nuevoNivel === nivel) return;
    if (editado && !window.confirm('Has editado el texto. Al cambiar de plantilla se sobrescribirán el asunto y el mensaje. Continuar?')) {
      return;
    }
    setNivel(nuevoNivel);
    cargarPlantilla(nuevoNivel);
  };

  const toggleContacto = (email) => {
    setCcSeleccionados(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const enviar = async () => {
    if (!emailDestino) { onAviso('error', 'Falta el email de destino'); return; }
    // Componer lista de CC final.
    const cc = [...ccSeleccionados];
    if (copiaInterna && !cc.includes('administracion@duettocatering.com')) cc.push('administracion@duettocatering.com');
    const libre = ccLibre.trim();
    if (libre && !cc.includes(libre)) cc.push(libre);
    if (!window.confirm(`Enviar el recordatorio a ${emailDestino}?`)) return;
    setEnviando(true);
    try {
      const res = await cobrosReclamacionAPI.enviar({
        factura_id: factura.factura_id,
        asunto,
        mensaje,
        email_destino: emailDestino,
        cc,
      });
      onAviso('ok', res.data?.mensaje || 'Recordatorio enviado');
      onEnviado();
    } catch (e) {
      onAviso('error', detalleError(e, 'No se pudo enviar el recordatorio'));
    }
    setEnviando(false);
  };

  return (
    <div data-testid="reclamacion-envio-form">
      {/* Cabecera del formulario */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <button onClick={onVolver} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="reclamacion-envio-volver">
          <ArrowRight className="w-4 h-4 rotate-180" />
          Volver
        </button>
        <span className="text-sm font-bold text-red-600">{formatCurrency(factura.importe_pendiente)}</span>
      </div>

      <div className="px-4 py-3 space-y-4">
        {/* Selector de plantilla */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Plantilla</label>
          <div className="flex gap-2">
            <button
              data-testid="plantilla-nivel-1"
              onClick={() => cambiarNivel(1)}
              disabled={loading}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${nivel === 1 ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              Primer aviso (amable)
            </button>
            <button
              data-testid="plantilla-nivel-2"
              onClick={() => cambiarNivel(2)}
              disabled={loading}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${nivel === 2 ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              Segundo aviso (firme)
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-400 text-sm">Cargando plantilla...</div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Para</label>
              <input
                data-testid="reclamar-email"
                type="email"
                value={emailDestino}
                onChange={(e) => setEmailDestino(e.target.value)}
                placeholder="cliente@ejemplo.com"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
              />
            </div>

            {/* CC */}
            <div data-testid="reclamar-cc">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">CC (copia)</label>
              <div className="space-y-1.5">
                {contactos.map((c) => (
                  <label key={c.email} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ccSeleccionados.includes(c.email)}
                      onChange={() => toggleContacto(c.email)}
                      className="rounded border-slate-300 text-amber-600 focus:ring-amber-400"
                    />
                    <span className="text-sm text-slate-700 truncate">
                      {c.nombre || c.email}
                      <span className="text-xs text-slate-400 ml-1">{c.email}{c.cargo ? ` · ${c.cargo}` : ''}</span>
                    </span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="reclamar-cc-interna"
                    type="checkbox"
                    checked={copiaInterna}
                    onChange={(e) => setCopiaInterna(e.target.checked)}
                    className="rounded border-slate-300 text-amber-600 focus:ring-amber-400"
                  />
                  <span className="text-sm text-slate-700">Enviarnos copia <span className="text-xs text-slate-400">(administracion@duettocatering.com)</span></span>
                </label>
                <input
                  data-testid="reclamar-cc-libre"
                  type="email"
                  value={ccLibre}
                  onChange={(e) => setCcLibre(e.target.value)}
                  placeholder="Añadir otro email en copia..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Asunto</label>
              <input
                data-testid="reclamar-asunto"
                type="text"
                value={asunto}
                onChange={(e) => { setAsunto(e.target.value); setEditado(true); }}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mensaje</label>
              <textarea
                data-testid="reclamar-mensaje"
                value={mensaje}
                onChange={(e) => { setMensaje(e.target.value); setEditado(true); }}
                rows={12}
                className="w-full border border-slate-200 rounded-lg p-3 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
              />
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5">
              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              El mensaje se enviará con la plantilla corporativa de Duetto (logo y firma) y el PDF de la factura {nombreAdjunto || factura.numero_factura} adjunto.
            </p>

            <button
              data-testid="reclamar-enviar"
              onClick={enviar}
              disabled={enviando || loading || !emailDestino}
              className="w-full flex items-center justify-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {enviando ? 'Enviando...' : 'Enviar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Seccion de observaciones dentro del panel de detalle de reclamacion.
function ReclamacionObservaciones({ facturaId, observaciones, onAviso, onGuardado }) {
  const [nuevaObs, setNuevaObs] = useState('');
  const [guardando, setGuardando] = useState(false);

  const anadirObservacion = async () => {
    const texto = nuevaObs.trim();
    if (!texto) return;
    setGuardando(true);
    try {
      await cobrosReclamacionAPI.observacion({ factura_id: facturaId, texto });
      setNuevaObs('');
      onGuardado();
    } catch (e) {
      onAviso('error', detalleError(e, 'No se pudo guardar la observación'));
    }
    setGuardando(false);
  };

  return (
    <div className="px-4 py-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Observaciones</p>
      {observaciones.length === 0 ? (
        <p className="text-sm text-slate-400 py-1 mb-2">Sin observaciones todavía.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {observaciones.map((o, i) => (
            <div key={i} className="bg-slate-50 rounded-lg px-3 py-2" data-testid={`observacion-item-${i}`}>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{o.texto}</p>
              <p className="text-[10px] text-slate-400 mt-1">
                {formatFechaHora(o.fecha)}{o.usuario_email ? ` · ${o.usuario_email}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}
      <textarea
        data-testid="nueva-observacion-input"
        value={nuevaObs}
        onChange={(e) => setNuevaObs(e.target.value)}
        placeholder="P. ej.: Pagan la semana que viene / No están conformes con el servicio..."
        className="w-full border border-slate-200 rounded-lg p-2.5 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
      />
      <button
        data-testid="anadir-observacion-btn"
        onClick={anadirObservacion}
        disabled={guardando || !nuevaObs.trim()}
        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        {guardando ? 'Guardando...' : 'Añadir observación'}
      </button>
    </div>
  );
}

// Modal para configurar la politica de pago del cliente (plazo, dia de pago, FACe)
const PLAZOS_ESTANDAR = [30, 45, 60, 90];

function PoliticaPagoModal({ factura, onClose, onGuardado, onError }) {
  const [plazoSelect, setPlazoSelect] = useState(''); // '' (sin configurar) | '30' | '45' | '60' | '90' | 'otro'
  const [plazoOtro, setPlazoOtro] = useState('');
  const [diaMes, setDiaMes] = useState('');
  const [face, setFace] = useState(false);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let activo = true;
    (async () => {
      setLoading(true);
      try {
        const res = await clientesAPI.getOne(factura.cliente_id);
        if (!activo) return;
        const c = res.data || {};
        const dias = c.pago_dias;
        // Si el cliente no tiene plazo configurado, el select arranca vacio
        // ("Sin configurar") en lugar de asumir 30 dias.
        if (dias === null || dias === undefined) {
          setPlazoSelect('');
          setPlazoOtro('');
        } else if (PLAZOS_ESTANDAR.includes(dias)) {
          setPlazoSelect(String(dias));
          setPlazoOtro('');
        } else {
          setPlazoSelect('otro');
          setPlazoOtro(String(dias));
        }
        setDiaMes(c.pago_dia_mes === null || c.pago_dia_mes === undefined ? '' : String(c.pago_dia_mes));
        setFace(Boolean(c.pago_face));
      } catch (e) {
        onError(detalleError(e, 'No se pudo cargar el cliente'));
      }
      if (activo) setLoading(false);
    })();
    return () => { activo = false; };
  }, [factura.cliente_id, onError]);

  const guardar = async () => {
    // Plazo: null cuando esta sin configurar; valor cuando 30/45/60/90 u otro
    let pagoDias = null;
    if (plazoSelect === 'otro') {
      const n = parseInt(plazoOtro, 10);
      if (isNaN(n) || n <= 0) { onError('Introduce un número de días válido'); return; }
      pagoDias = n;
    } else if (plazoSelect !== '') {
      pagoDias = parseInt(plazoSelect, 10);
    }

    let pagoDiaMes = null;
    if (String(diaMes).trim() !== '') {
      const dm = parseInt(diaMes, 10);
      if (isNaN(dm) || dm < 1 || dm > 31) { onError('El día de pago debe estar entre 1 y 31'); return; }
      pagoDiaMes = dm;
    }

    setGuardando(true);
    try {
      await cobrosReclamacionAPI.politica(factura.cliente_id, {
        pago_dias: pagoDias,
        pago_dia_mes: pagoDiaMes,
        pago_face: face,
      });
      onGuardado('Política de pago actualizada');
    } catch (e) {
      onError(detalleError(e, 'No se pudo guardar la política de pago'));
    }
    setGuardando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="politica-pago-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-500" />
              Política de pago
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{factura.cliente_nombre}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0" data-testid="politica-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Cargando datos del cliente...</div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Plazo de pago (días)</label>
                <select
                  data-testid="politica-plazo"
                  value={plazoSelect}
                  onChange={(e) => setPlazoSelect(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
                >
                  <option value="">Sin configurar</option>
                  <option value="30">30 días (estándar)</option>
                  <option value="45">45 días</option>
                  <option value="60">60 días</option>
                  <option value="90">90 días</option>
                  <option value="otro">Otro</option>
                </select>
                {plazoSelect === 'otro' && (
                  <input
                    data-testid="politica-plazo-otro"
                    type="number"
                    min="1"
                    value={plazoOtro}
                    onChange={(e) => setPlazoOtro(e.target.value)}
                    placeholder="Días"
                    className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Día de pago del mes</label>
                <input
                  data-testid="politica-dia-mes"
                  type="number"
                  min="1"
                  max="31"
                  value={diaMes}
                  onChange={(e) => setDiaMes(e.target.value)}
                  placeholder="Ej.: 10"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
                />
                <p className="text-xs text-slate-500 mt-1">Déjalo vacío si no tienen día fijo. Ej.: pagan los días 10.</p>
              </div>

              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="politica-face"
                    type="checkbox"
                    checked={face}
                    onChange={(e) => setFace(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-400"
                  />
                  <span className="text-sm text-slate-700">
                    Facturación vía FACe (administración pública)
                    <span className="block text-xs text-slate-500 mt-0.5">Sin reclamación automática por Telegram; el plazo orientativo pasa a 90 días.</span>
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50" data-testid="politica-cancelar">
            Cancelar
          </button>
          <button
            data-testid="politica-guardar"
            onClick={guardar}
            disabled={guardando || loading}
            className="px-5 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
